import type { AIProviderId } from "./aiProviders";
import { getAIProvider } from "./aiProviders";
import { isSqliteMediaType } from "./fileMediaTypes";

export { isSqliteMediaType, mediaTypeFromFileName } from "./fileMediaTypes";

export const IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
]);

export const TEXT_LIKE_TYPES = new Set([
  "text/plain",
  "text/markdown",
  "text/csv",
  "text/html",
  "text/xml",
  "application/json",
  "application/jsonl",
  "application/toml",
  "application/xml",
  "application/yaml",
]);

const CSV_TYPES = new Set(["text/csv"]);

const ARCHIVE_TYPES = new Set([
  "application/zip",
  "application/x-zip-compressed",
  "application/x-tar",
  "application/gzip",
  "application/x-gzip",
]);

export type PreviewKind = "image" | "text" | "csv" | "sqlite" | "fallback";

export function isCsvMediaType(mediaType: string): boolean {
  return CSV_TYPES.has(mediaType);
}

export function isArchiveMediaType(mediaType: string): boolean {
  return ARCHIVE_TYPES.has(mediaType);
}

export function previewKind(mediaType: string): PreviewKind {
  if (IMAGE_TYPES.has(mediaType)) return "image";
  if (isSqliteMediaType(mediaType)) return "sqlite";
  if (isCsvMediaType(mediaType)) return "csv";
  if (TEXT_LIKE_TYPES.has(mediaType) || mediaType.startsWith("text/")) {
    return "text";
  }
  return "fallback";
}

export const MAX_IMAGE_SIZE = Math.floor((5 * 1024 * 1024 * 3) / 4);
export const MAX_PDF_SIZE = 32 * 1024 * 1024;
export const MAX_DOCUMENT_SIZE = Math.floor(4.5 * 1024 * 1024);

function getAttachmentSupport(providerId: AIProviderId) {
  return getAIProvider(providerId).attachmentSupport;
}

export function attachmentHandling(
  providerId: AIProviderId,
  mediaType: string
): "image" | "file" | "inline-text" | null {
  if (IMAGE_TYPES.has(mediaType)) return "image";
  const support = getAttachmentSupport(providerId);
  if (support.inlineTextMediaTypes?.includes(mediaType)) return "inline-text";
  return support.documentMediaTypes.includes(mediaType) ? "file" : null;
}

export function isSupportedAttachmentMediaType(
  providerId: AIProviderId,
  mediaType: string
) {
  return attachmentHandling(providerId, mediaType) != null;
}

export function resolveAttachmentMediaType(
  file: Pick<File, "name" | "type">,
  providerId: AIProviderId
) {
  if (IMAGE_TYPES.has(file.type)) return file.type;
  if (getAttachmentSupport(providerId).documentMediaTypes.includes(file.type)) {
    return file.type;
  }

  const extension = file.name.split(".").pop()?.toLowerCase();
  if (!extension) return null;

  return (
    getAttachmentSupport(providerId).extensionToMediaType[extension] ?? null
  );
}

export function attachmentSizeLimit(mediaType: string) {
  if (IMAGE_TYPES.has(mediaType)) return MAX_IMAGE_SIZE;
  if (mediaType === "application/pdf") return MAX_PDF_SIZE;
  return MAX_DOCUMENT_SIZE;
}
